import React, { useState } from "react";
import { Button } from "@/aible";
import { isEnabled } from "@/utils/featureFlags";
import { getComparisonOptions, openCreateModels, openExploration } from "@/containers/Next/NextReducer";
import { useDispatch, useSelector } from "@/types/store";
import { getDateAnalysis, getIsLoadingChat, getIsLoadingCreateAI, getIsLoadingExplore, getMinimumValueCount, getOutcome } from "@/containers/Sense/dataset-selectors";
import { updateTileState } from "@/containers/Sense/reducer";
import { getDataset, getDefaultSuccess, getIsKPIAnalysis, getIsKPIReady, getKPIAnalysisDateColumn, getKPIDateRange, getSuccessOptions } from "@/containers/Home/reducer";
import { calculateDateValues } from "@/components/Tiles/Sense/ActionsPanel";
import { getDefaultDateIndices, handleSelectUseCase, initKPIAnalysis } from "@/containers/ChatGPT/reducer";
import { updateAgentAutonomyView } from "@/containers/View/reducer";
const StructuredRowHoverActions = ({ datasetId }) => {
    const dispatch = useDispatch();
    const updateState = (update) => {
        dispatch(updateTileState(datasetId, update));
    };
    const dateAnalysisState = useSelector((state) => getDateAnalysis(state, datasetId));
    const dataset = useSelector((state) => getDataset(state, datasetId));
    const isLoadingExplore = useSelector((state) => getIsLoadingExplore(state, datasetId));
    const updateIsLoadingExplore = (isLoadingExplore) => updateState({ isLoadingExplore });
    const isLoadingCreateAI = useSelector((state) => getIsLoadingCreateAI(state, datasetId));
    const updateIsLoadingCreateAI = (isLoadingCreateAI) => updateState({ isLoadingCreateAI });
    const minimumValueCounts = useSelector((state) => getMinimumValueCount(state, datasetId));
    const successOptions = useSelector((state) => getSuccessOptions(state, datasetId, dataset, dataset?.outcome));
    const outcomeState = useSelector((state) => getOutcome(state, datasetId));
    const outcomeSuccess = outcomeState.outcomeSuccess;
    const outcomeField = dataset?.fields?.[dataset?.outcome ?? ""];
    const outcomeOptions = outcomeField?.type !== "number" && outcomeField?.data
        ? successOptions?.map((o) => ({
            value: o.value,
            label: o.label
        }))
        : undefined;
    const defaultSuccess = useSelector((state) => getDefaultSuccess(state, datasetId));
    const outcomeValue = outcomeSuccess ?? outcomeOptions?.find((o) => o?.value === defaultSuccess) ?? outcomeOptions?.[0];
    const { dateField, dateAnalysisTarget } = calculateDateValues(dataset, dateAnalysisState);
    const showKpiOverview = useSelector((state) => getIsKPIReady(state, datasetId));
    const datasetFields = Object.values(dataset?.fields ?? {});
    const retDatasetFields = dataset?.ranked_fields ?? datasetFields;
    const datasetDateOptions = retDatasetFields.filter((f) => f.type === "date");
    const showKpiWhatsChanged = Boolean(isEnabled("dataset-whats-changed") && datasetDateOptions?.length > 0);
    const kpiNextId = "DATASET_" + datasetId;
    const comparisonOptions = useSelector((state) => getComparisonOptions(state, kpiNextId));
    const dateColumn = useSelector((state) => getKPIAnalysisDateColumn(state, datasetId));
    const KPIDateRange = useSelector((state) => getKPIDateRange(state, datasetId));
    const datasetDateField = dataset?.options?.timestamp_column;
    const dateFieldOptions = dataset?.ranked_fields !== undefined
        ? dataset.ranked_fields
            .filter((f) => f.type === "date")
            .map((f) => ({
            value: f,
            label: f.label
        }))
        : [];
    const updateDateAnalysisState = (dateAnalysis) => {
        if (dataset?.id) {
            dispatch(updateTileState(dataset?.id, { dateAnalysis }));
        }
    };
    const datasetTimeBucket = dataset?.options?.kpi_analysis?.time_bucket;
    const checkComparisonOptions = () => {
        const retComparisonOptions = {
            ...KPIDateRange,
            ...comparisonOptions
        };
        // Add this comparisonOptions check which is using the kpiNextId as an argument in getComparisonOptions selector so that this only updates once
        // and prevents the component rerendering from search filtering from dispatching all over again
        if (!comparisonOptions) {
            if (!retComparisonOptions.dateField) {
                if (dateColumn) {
                    retComparisonOptions.dateField = dateColumn;
                }
                else if (datasetDateField) {
                    retComparisonOptions.dateField = datasetDateField;
                }
                else {
                    retComparisonOptions.dateField = dateFieldOptions?.[0]?.label;
                }
            }
            let fields = dataset?.ranked_fields;
            if (!fields && dataset?.fields) {
                fields = Object.keys(dataset.fields).map((fKey) => dataset.fields[fKey]);
            }
            // Null check due to this component being rendered before dataset details finishes downloading
            if (!fields) {
                fields = [];
            }
            const dates = fields.find((f) => f.label === retComparisonOptions?.dateField)?.data;
            const filteredDates = (dates ?? [])?.filter((d) => d?.value !== "{Missing}");
            const checkIsMissingDateOption = (datesArr) => {
                const isMissing = datesArr.some((date) => {
                    if (date === undefined) {
                        return true;
                    }
                    const foundDate = filteredDates?.find((fDate) => fDate?.value === date);
                    if (!foundDate) {
                        return true;
                    }
                    return false;
                });
                return isMissing;
            };
            const comparisonDates = [
                retComparisonOptions?.currentEnd,
                retComparisonOptions?.currentStart,
                retComparisonOptions?.referenceEnd,
                retComparisonOptions?.referenceStart
            ];
            const missingDateRange = checkIsMissingDateOption(comparisonDates);
            // Populating the initial comparisonOption date references if they don't exist or if a date is not found in
            // filteredDates (fixes broken state but now requires all valid dates to be saved correctly).
            // Using new function to check default date indices, 3 months start/end, 4 weeks start/end, 7 days (or whatever date unit) start/end otherwise
            if (retComparisonOptions?.dateField && missingDateRange) {
                const { refStart, refEnd, currStart, currEnd } = getDefaultDateIndices(filteredDates, datasetTimeBucket);
                retComparisonOptions.referenceStart = filteredDates[refStart]?.value;
                retComparisonOptions.referenceEnd = filteredDates[refEnd]?.value;
                retComparisonOptions.currentStart = filteredDates[currStart]?.value;
                retComparisonOptions.currentEnd = filteredDates[currEnd]?.value;
            }
        }
        updateDateAnalysisState({ ...retComparisonOptions });
        return retComparisonOptions;
    };
    const [isLoadingKpi, updateIsLoadingKpi] = useState(false);
    const handleStartKpiWhatsChanged = () => {
        updateIsLoadingKpi(true);
        const newComparisonOptions = checkComparisonOptions();
        dispatch(openExploration(dataset?.id, outcomeValue, newComparisonOptions, 0))
            .catch((e) => console.log(e))
            .finally(() => updateIsLoadingKpi(false));
    };
    const disableKpiWhatsChanged = !datasetTimeBucket;
    const isKPIAnalysis = useSelector((state) => getIsKPIAnalysis(state, dataset?.id ?? -1));
    const isLoadingChat = useSelector((state) => getIsLoadingChat(state, datasetId));
    return (React.createElement(React.Fragment, null,
        isEnabled("chat-aible") && (React.createElement(Button, { type: "Secondary", isLoading: isLoadingChat, onClick: () => {
                if (isEnabled("agent-autonomy-controls")) {
                    dispatch(updateAgentAutonomyView("RECOMMENDED"));
                }
                dispatch(handleSelectUseCase("CHATAIBLE_ANALYTICS", dataset?.id));
            } }, "Agentic Analytics Chat")),
        isEnabled("document-set-kpi-analysis") && isKPIAnalysis && showKpiOverview && (React.createElement(Button, { type: "Secondary", onClick: () => {
                const newComparisonOptions = checkComparisonOptions();
                if (dataset?.id) {
                    dispatch(initKPIAnalysis(dataset?.id, true, newComparisonOptions));
                }
            }, isLoading: isLoadingChat }, "What's Changed Overview")),
        showKpiWhatsChanged && (React.createElement(Button, { type: "Secondary", onClick: handleStartKpiWhatsChanged, isLoading: isLoadingKpi && showKpiWhatsChanged, disabled: disableKpiWhatsChanged }, "What's Changed Deep Dive")),
        (isEnabled("aible-optimize") || isEnabled("aible-explore")) && (React.createElement(Button, { type: "Secondary", onClick: () => {
                updateIsLoadingExplore(true);
                setTimeout(() => dispatch(openExploration(datasetId, outcomeValue, undefined, minimumValueCounts))
                    .catch((e) => console.log(e))
                    .finally(() => updateIsLoadingExplore(false)), 5);
            }, isLoading: isLoadingExplore }, "Explore Insights")),
        isEnabled("aible-optimize") && !isEnabled("aible-explore") && (React.createElement(Button, { type: "Secondary", onClick: () => {
                updateIsLoadingCreateAI(true);
                setTimeout(() => {
                    dispatch(openCreateModels(dataset?.id, outcomeValue, dateAnalysisTarget === "CREATE_AI" || dateAnalysisTarget === "BOTH"
                        ? {
                            dateField: dateField?.label,
                            currentStart: dateAnalysisState?.currentStart,
                            currentEnd: dateAnalysisState?.currentEnd,
                            referenceStart: dateAnalysisState?.referenceStart,
                            referenceEnd: dateAnalysisState?.referenceEnd
                        }
                        : undefined))
                        .catch((e) => console.log(e))
                        .finally(() => updateIsLoadingCreateAI(false));
                }, 5);
            }, isLoading: isLoadingCreateAI }, "Create AI"))));
};
export default StructuredRowHoverActions;
